import React, { useState, useCallback, useRef, useEffect } from 'react';
import { MessageSquare, X, Send, Sparkles, Minus } from 'lucide-react';
import { useAskAnalyst } from '../../context/AskAnalystContext';
import { sendAgentMessage } from '../../utils/agentApi';

const STARTER_PROMPTS = [
  'Is our validation MAPE good enough to trust budget recommendations?',
  'Why is Bing at high saturation but budget still increasing?',
  'Google Non-Brand vs Bing Brand — why is cost/PT so different?',
  'How much of the late 2025 PT drop was macro vs media?',
  'Forward Planner shows $0 in Q1 FY28 — data gap or intentional?',
];

const AskAnalystPanel = () => {
  const { panelState, closePanel, minimizePanel, context } = useAskAnalyst();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);
  const conversationIdRef = useRef(null);
  const prevPanelStateRef = useRef('closed');

  useEffect(() => {
    if (panelState === 'open') bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, panelState, loading]);

  useEffect(() => {
    if (prevPanelStateRef.current === 'closed' && panelState === 'open') {
      setMessages([]);
      setError(null);
      conversationIdRef.current = crypto.randomUUID();
    }
    prevPanelStateRef.current = panelState;
  }, [panelState]);

  const sendMessage = useCallback(async (text) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;

    setLoading(true);
    setError(null);
    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);

    try {
      const result = await sendAgentMessage(trimmed, context, conversationIdRef.current);
      const assistantContent = formatResponse(result);
      setMessages((prev) => [...prev, { role: 'assistant', content: assistantContent, raw: result }]);
    } catch (err) {
      setError(err.message || 'Failed to get response');
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Sorry, I could not process that request. Please try again.' },
      ]);
    } finally {
      setLoading(false);
      setInput('');
    }
  }, [context, loading]);

  if (panelState !== 'open') return null;

  return (
    <div className="fixed top-20 right-0 bottom-0 z-50 flex w-full max-w-md flex-col border-l border-mongodb-gray-light2 bg-white shadow-2xl">
      <div className="flex items-center justify-between bg-mongodb-green-dark2 px-4 py-3 text-white">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Sparkles className="h-5 w-5" />
          Ask Analyst
        </h2>
        <div className="flex items-center gap-1">
          <button type="button" onClick={minimizePanel} className="rounded p-1 hover:bg-white/20" aria-label="Minimize">
            <Minus className="h-5 w-5" />
          </button>
          <button type="button" onClick={closePanel} className="rounded p-1 hover:bg-white/20" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="border-b border-mongodb-gray-light2 bg-mongodb-gray-light3 px-4 py-2 text-xs text-mongodb-gray-dark1">
        Context: {context.region} · {context.quarter}
        {context.activeTab ? ` · ${context.activeTab}` : ''}
      </div>

      <div className="flex-1 overflow-y-auto bg-mongodb-gray-light3 p-4 space-y-4">
        {messages.length === 0 && (
          <div>
            <p className="mb-3 text-sm text-mongodb-gray-dark2">Ask about MMM metrics, budgets, or methodology:</p>
            <div className="flex flex-col gap-2">
              {STARTER_PROMPTS.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  onClick={() => sendMessage(prompt)}
                  className="rounded-lg border border-mongodb-gray-light2 bg-white px-3 py-2 text-left text-sm text-mongodb-gray-dark3 hover:border-mongodb-green-dark2 hover:bg-mongodb-green-light3"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={msg.role === 'user' ? 'text-right' : 'text-left'}>
            <div
              className={`inline-block max-w-[90%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                msg.role === 'user'
                  ? 'bg-mongodb-green-dark2 text-white'
                  : 'border border-mongodb-gray-light2 bg-white text-mongodb-gray-dark3'
              }`}
            >
              {msg.content}
            </div>
          </div>
        ))}

        {loading && (
          <div className="text-sm text-mongodb-gray-dark1 animate-pulse">Analyzing your data...</div>
        )}
        {error && <div className="text-sm text-mongodb-red-dark2">{error}</div>}
        <div ref={bottomRef} />
      </div>

      <div className="border-t border-mongodb-gray-light2 bg-white p-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendMessage(input)}
            placeholder="Ask a business question..."
            className="flex-1 rounded-lg border border-mongodb-gray-light1 px-3 py-2 text-sm text-mongodb-gray-dark3 focus:border-mongodb-green-dark2 focus:outline-none focus:ring-2 focus:ring-mongodb-green-dark2/20"
            disabled={loading}
          />
          <button
            type="button"
            onClick={() => sendMessage(input)}
            disabled={loading || !input.trim()}
            className="rounded-lg bg-mongodb-green-dark2 px-3 py-2 text-white disabled:opacity-50 hover:bg-mongodb-green-dark1"
            aria-label="Send"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

function formatResponse(result) {
  const parts = [];
  if (result.summary) parts.push(result.summary);

  if (result.insights?.length) {
    parts.push('\n\nKey findings:');
    result.insights.forEach((ins) => {
      parts.push(`• ${ins.label}: ${ins.value}`);
    });
  }

  if (result.actions?.length) {
    parts.push('\n\nRecommended actions:');
    result.actions.forEach((a) => parts.push(`• ${a}`));
  }

  if (result.sources?.length) {
    parts.push('\n\nSources:');
    result.sources.forEach((s) => {
      if (s.type === 'tool') parts.push(`• Data: ${s.name}`);
      else parts.push(`• Methodology: ${s.name}`);
    });
  }

  if (result.trace_id) parts.push(`\n\nTrace: ${result.trace_id}`);
  return parts.join('\n');
}

export const AskAnalystButton = ({ className = '' }) => {
  const { panelState, togglePanel } = useAskAnalyst();
  const isActive = panelState !== 'closed';
  const isMinimized = panelState === 'minimized';
  return (
    <button
      type="button"
      onClick={togglePanel}
      className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-md transition-colors ${
        isMinimized
          ? 'bg-mongodb-green-dark1 ring-2 ring-white animate-pulse hover:bg-mongodb-green-dark2'
          : isActive
            ? 'bg-mongodb-green-dark1 ring-2 ring-white/50 hover:bg-mongodb-green-dark2'
            : 'bg-mongodb-green-dark2 hover:bg-mongodb-green-dark1'
      } ${className}`}
    >
      <MessageSquare className="h-4 w-4" />
      Ask Analyst
    </button>
  );
};

export default AskAnalystPanel;
